import { useSuggestAddress } from '../hooks';
import { Icon } from 'app/common/components';
import React, { useContext, useMemo } from 'react';
import { SuggestionAutoComplete } from './SuggestionAutoComplete';
import { LocationPin } from './LocationPin';
import { useFormatMessage } from 'app/common/hooks';
import messages from 'app/layout/components/Header/components/StoreDropdown/StoreDropdown.messages';
import { LocationQueryInput } from './LocationQueryInput';
import { FlipSwitch } from './FlipSwitch.js';
import { AuroraPayImageBlue } from 'app/checkout/utils/PaymentMethodUtil.js';
import { get } from 'lodash';
import { CustomerContext } from 'app/common/contexts';
import { Environment } from 'app/common/services';

/**
 * Renders the location query component. This component is used to search for stores by zip code, city, state, etc.
 * Also renders the "Use My Location" button which attempts to find stores using the browser location API if available.
 * @param {LocationQueryInputProps} locationQueryInputProps The props for the location query input component
 * @param {SuggestAddressProps} suggestAddressProps The props for the suggest address component
 * @param {boolean} isModalInput Is this component being used in a modal?
 * @param {function} getLocationOnClick The callback to handle the "Use My Location" button click
 * @param {function} closeButtonOnClick The callback to handle the close button click
 * @param {SuggestionsAutoCompleteProps} suggestionAutoCompleteProps The props for the suggestion autocomplete component
 * @param {function} auroraPayFilter Callback to apply the AuroraPay filter
 * @param {function} clearFilters Callback to clear the filters
 * @returns {Element}
 * @constructor
 */
const LocationQuery = ({
  locationQueryInputProps,
  suggestAddressProps,
  isModalInput = false,
  getLocationOnClick,
  closeButtonOnClick,
  suggestionAutoCompleteProps,
  auroraPayFilter = () => {},
  clearFilters = () => {}
}) => {
  useSuggestAddress(suggestAddressProps);
  const formatMessage = useFormatMessage();

  const { customer } = useContext(CustomerContext);

  const showAuroraPayFilter = useMemo(() => {
    if (customer) {
      return (
        auroraPayEnabled() &&
        get(customer, 'attributes.fleetPerformEnabled', false)
      );
    }
    return false;
  }, [customer]);

  return (
    <div className={'flex-row pb-4'}>
      <div className={'flex flex-col'}>
        <div className={'flex flex-col lg:flex-row pb-4 pt-3'}>
          <div
            className={
              'find-other-stores flex flex-grow flex-row -mt-6 lg:mt-0'
            }
          >
            <div className={'flex pr-2 font-bold'}>
              {formatMessage(messages.findOtherStores)}
            </div>
            <div className={'flex pr-2'}>|</div>
            <div className={'flex flex-row text-primary-500'}>
              <button onClick={getLocationOnClick} className={'flex'}>
                <div className={'flex h-full'}>
                  <LocationPin className={'pr-2'} color={'#1A6E99'} />
                </div>
                <div className={'flex text-primary-500'}>
                  {formatMessage(messages.useMyLocation)}
                </div>
              </button>
            </div>
          </div>
          <div
            className={
              'dropdown-aurorapay-filter flex flex-row lg:justify-end mt-2 lg:mt-0'
            }
          >
            <div className={'text-center text-sm font-semibold'}>
              {showAuroraPayFilter && formatMessage(messages.showOnly)}
            </div>
            <div className={'flex flex-row justify-center'}>
              {showAuroraPayFilter && (
                <>
                  <AuroraPayImageBlue className={'relative h-4 px-2'} />
                  <FlipSwitch
                    onCheck={auroraPayFilter}
                    onUnCheck={clearFilters}
                  />
                </>
              )}
            </div>
          </div>
          <div
            className={
              'dropdown-close flex items-center justify-end flex-grow order-first lg:order-last'
            }
          >
            {!isModalInput && (
              <button
                className={'flex items-center justify-end w-1/5 pr-3 z-10'}
                onClick={closeButtonOnClick}
                aria-label={formatMessage(messages.closeLocationSearch)}
                title={formatMessage(messages.closeLocationSearch)}
              >
                <Icon name={'times'} className={'fa-xl'} />
              </button>
            )}
          </div>
        </div>
        <div
          className={'flex-row pr-2'}
          ref={suggestionAutoCompleteProps.suggestionContainerRef}
        >
          <LocationQueryInput {...locationQueryInputProps} />
          <SuggestionAutoComplete {...suggestionAutoCompleteProps} />
        </div>
      </div>
    </div>
  );
};

function auroraPayEnabled() {
  return Environment.get('AURORA_PAY_ENABLED', 'false') === 'true';
}

export { LocationQuery };
